import React from "react";
import { FormFieldsMap } from "features/shared/forms/dynamicForm/fields/FormFieldsMap";
import BaseFormFieldProps from "features/shared/forms/dynamicForm/models/BaseFormFieldProps";
import FormFieldDescriptor from "features/shared/forms/dynamicForm/models/FormFieldDescriptor";
import {
  SetValidationRuleFunction,
  ValidationActionType,
} from "features/shared/forms/hooks/useForm";
import { TFunction } from "i18next";
import FormGroupTooltip from "features/shared/forms/components/formGroup/FormGroupTooltip";
import InputGroupBaseProps from "features/shared/forms/components/inputGroup/InputGroupBaseProps";
import OptionType from "features/shared/components/selectInput/OptionType";
import { getEnumValuesByKey } from "features/shared/helpers/enumHelper";
import { containsKey } from "features/shared/helpers/objects";

export const renderDynamicFormFields = (
  formFields: FormFieldDescriptor[],
  fieldPros: BaseFormFieldProps
): JSX.Element[] => {
  let result: JSX.Element[] = [];

  for (let i = 0; i < formFields.length; i++) {
    const descriptor = formFields[i];
    const fieldToRender = FormFieldsMap[descriptor.type];

    if (fieldToRender !== undefined) {
      const elem = fieldToRender({ descriptor: descriptor, ...fieldPros });
      result.push(<React.Fragment key={descriptor.name} children={elem} />);
    } else {
      console.warn(`Field with type '${descriptor.type}' doesn't exists.`);
    }
  }

  return result;
};

export const setDynamicFormValidationRules = (
  formFields: FormFieldDescriptor[],
  setValidationRule: SetValidationRuleFunction,
  actionType: ValidationActionType = "add"
) => {
  for (const descriptor of formFields) {
    if (descriptor.validation) {
      setValidationRule(descriptor.name, descriptor.validation, actionType);
    }
  }
};

export const getTranslatedText = (
  descriptor: FormFieldDescriptor,
  fieldName: "label" | "placeholder",
  translate: TFunction
) => {
  let label = (descriptor as any)[fieldName];
  if (descriptor.translationPrefix) {
    let translationCode = `${descriptor.translationPrefix}.${descriptor.name}.${fieldName}`;
    let translation = translate(translationCode);
    label = translation === translationCode ? label : translation;
  }

  return label;
};

export const getTranslatedTooltip = (
  descriptor: FormFieldDescriptor,
  translate: TFunction
): FormGroupTooltip | undefined => {
  if (descriptor.tooltip) {
    let tooltip: FormGroupTooltip = { ...descriptor.tooltip };
    let content = descriptor.tooltip.content;
    if (descriptor.translationPrefix) {
      let translationCode = `${descriptor.translationPrefix}.${descriptor.name}.tooltip`;
      let translation = translate(translationCode);
      content = translation === translationCode ? content : translation;
      tooltip.content = content;
    }

    return tooltip;
  }

  return undefined;
};

export const fillExternalData = (
  descriptors: FormFieldDescriptor[],
  externalData: Record<string, any[]>
) => {
  if (descriptors.length > 0 && externalData) {
    for (const descriptor of descriptors) {
      if (descriptor.options?.source === "external") {
        let data = externalData[descriptor.name];
        if (data) {
          descriptor.options.options = data;
        }
      }
    }
  }
};

export const getSelectFieldOptions = <T,>(
  descriptor: FormFieldDescriptor,
  data?: T[],
  tEnums?: TFunction
): T[] => {
  if (descriptor.options) {
    switch (descriptor.options.source) {
      case "fetch":
        return data ?? [];
      case "external":
        return descriptor.options.options
          ? (descriptor.options.options as T[])
          : [];
      case "options":
        const options = getOptions(descriptor);
        return options as T[];
      case "enum":
        const enumOptions = getOptionsByEnum(descriptor, tEnums);
        return enumOptions as T[];
      default:
        console.log(
          `Source type '${descriptor.options.source}' is not supported for ${descriptor.type} (descriptor: ${descriptor.name}).`
        );
        return [];
    }
  } else {
    console.log(
      `Badly defined values for ContractSelectField (descriptor: ${descriptor.name}).`
    );
  }

  return [];
};

export const getSelectFieldOptionKey = <T,>(
  key: string | undefined,
  defaultKey: keyof T
): keyof T => {
  if (key) {
    return key as keyof T;
  }

  return defaultKey;
};

export const getInputGroupProps = (
  descriptor: FormFieldDescriptor
): InputGroupBaseProps => {
  let props: InputGroupBaseProps = {};
  const inputGroupDesc = descriptor.inputGroup;

  if (inputGroupDesc) {
    props.placement = inputGroupDesc.placement;
    props.contentText =
      inputGroupDesc.contentType === "text"
        ? inputGroupDesc.content
        : undefined;
    props.contentIcon =
      inputGroupDesc.contentType === "icon"
        ? (inputGroupDesc.content as any)
        : undefined;
  }

  return props;
};

// PRIVATE FUNCTIONS
const getOptions = (descriptor: FormFieldDescriptor): OptionType[] => {
  if (descriptor.options?.options) {
    const option = descriptor.options?.options[0];
    if (option && containsKey(option, "value") && containsKey(option, "name")) {
      const options = descriptor.options.options as OptionType[];
      const filterOptions = options.filter((x) => x.hidden !== true);
      return filterOptions;
    }
  }

  console.log(
    `Badly defined options for select field (descriptor: ${descriptor.name}).`
  );
  return [];
};

const getOptionsByEnum = (
  descriptor: FormFieldDescriptor,
  tEnums?: TFunction
): OptionType[] => {
  let result: OptionType[] = [];

  if (tEnums === undefined) {
    console.log(
      `Not given translate function for select field (descriptor: ${descriptor.name}).`
    );
    return result;
  }

  if (descriptor.options && descriptor.options.enumName) {
    let enumValues = getEnumValuesByKey(descriptor.options.enumName);
    if (enumValues.length === 0) {
      console.log(
        `Not defined mapping for enum select field (descriptor: ${descriptor.name}, enum name: ${descriptor.options.enumName}).`
      );
    }

    for (const value of enumValues) {
      let option: OptionType = { value: value, name: tEnums(value) };
      if (!isHiddenEnumOption(option, descriptor)) {
        result.push(option);
      }
    }
  } else {
    console.log(
      `Badly defined options for select field (descriptor: ${descriptor.name}).`
    );
  }

  return result;
};

const isHiddenEnumOption = (
  option: OptionType,
  descriptor: FormFieldDescriptor
): boolean => {
  if (descriptor?.options?.options) {
    let options = descriptor.options.options as OptionType[];
    let findOption = options.find((x) => x.value === option.value);
    return findOption && findOption.hidden === true ? true : false;
  }

  return false;
};
